import React, { useRef } from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Typography from '@/v2/components/common/Typography'
import { useMessagesContext } from '@/v2/contexts/MessagesContext'

const SubmitMessage = styled.div`
  background: ${({ theme }) => theme.background.default};
  padding: 10px 0 16px 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #eee;
  }
`

const HelpText = styled(Typography)`
  @media (max-width: 768px) {
    display: none;
  }
`

const SendMessage = ({ onSend }) => {
  const theme = useTheme()
  const { message, patient, onSetMessage } = useMessagesContext()
  const inputRef = useRef(null)

  const handleSubmit = () => {
    if (message.trim().length === 0) return
    if (!patient?.intakeForm?.uuid && message.indexOf('{HH form link}') >= 0)
      return alert("This patient doesn't have an intake form link to send.")

    onSend(
      message.replace(
        /{HH form link}/g,
        patient?.intakeForm?.status === 'completed'
          ? `https://smilebar.clearworks.com/intake_forms/${patient?.intakeForm?.id}`
          : `https://smilebar.clearworks.com/intake_forms/${patient?.intakeForm?.uuid}/intake_responses/new`
      )
    )

    onSetMessage('')
  }

  const handleKeyDown = event => {
    const shift = event.shiftKey
    const enter = event.key === 'Enter'

    if (shift && enter) {
      event.preventDefault()
      handleSubmit()
    }
  }

  // Check if on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768

  const handleFocus = () => {
    if (isMobile) {
      // Aguarda o teclado abrir e faz scroll para o input
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 300)
    }
  }

  return (
    <SubmitMessage theme={theme.palette} ref={inputRef}>
      <OutlinedInput
        fullWidth
        multiline
        autoFocus={!isMobile}
        maxRows={isMobile ? 4 : 6}
        value={message}
        placeholder={
          isMobile
            ? 'Type a message...'
            : 'Type a message and press shift+enter to send'
        }
        onFocus={handleFocus}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              edge="end"
              onClick={handleSubmit}
              onMouseDown={e => e.preventDefault()}
              disabled={!message.trim()}
              sx={{
                background: message.trim()
                  ? theme.palette.primary.main
                  : theme.palette.grey[300],
                color: message.trim()
                  ? `${theme.palette.primary.contrastText} !important`
                  : `${theme.palette.grey[500]} !important`,
                borderRadius: { xs: '50%', sm: '0' },
                marginLeft: { xs: '8px', sm: '-40px' },
                marginRight: { xs: '-8px', sm: '0' },
                height: { xs: '40px', sm: '80px', md: '120px' },
                width: { xs: '40px', sm: 'auto' },
                minWidth: { xs: '40px', sm: 'auto' },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </InputAdornment>
        }
        onChange={e => onSetMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{
          overflow: 'hidden',
          mb: { xs: 0, md: '10px' },
          padding: '2px 0 4px 0',
          '& .MuiOutlinedInput-input': {
            fontSize: { xs: '16px', sm: '14px' },
          },
        }}
      />
      <HelpText fontSize="12px" color="#7D7D7D">
        <b>Enter</b> adds a new line, <b>Shift + Enter</b> sends,{' '}
        <b>{'{HH form link}'}</b> inserts the patient Health History form link.
      </HelpText>
    </SubmitMessage>
  )
}

export default SendMessage
